import React, { useState } from "react";
import { REGEX } from "../../utils/regex";
import { ChevronDown } from "lucide-react";

const formatText = (text = "") =>
  text
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const FormField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  options = [],
  required = false,
  minLength,
  maxLength,
  min,
  max,
  rows = "1",
  disabled = false,
  searchable = false,
  error = "",
}) => {
  const [internalError, setInternalError] = useState("");
  const displayError = error || internalError;

  // const [search, setSearch] = useState("");

  const patterns = {
    string: /^[A-Za-z ]*$/,
    number: /^[0-9]*$/,
    alphanumeric: /^[A-Za-z0-9 ]*$/,
    pancard: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    email: REGEX.EMAIL,
  };

  const validate = (val) => {
    let err = "";

    if (required && !val) {
      err = `${label} is required`;
    } else if (type === "string" && val && !patterns.string.test(val)) {
      err = "Only alphabets allowed";
    } else if (type === "number" && val && !patterns.number.test(val)) {
      err = "Only digits allowed";
    } else if (
      type === "alphanumeric" &&
      val &&
      !patterns.alphanumeric.test(val)
    ) {
      err = "Only alphabets & numbers allowed";
    } else if (
      type === "pancard" &&
      val &&
      !patterns.pancard.test(val.toUpperCase())
    ) {
      err = "Invalid PAN format (ABCDE1234F)";
    } else if (type === "gst" && val && !patterns.gst.test(val.toUpperCase())) {
      err = "Invalid GST format";
    } else if (
      type === "email" &&
      val &&
      !patterns.email.test(val.toLowerCase())
    ) {
      err = "Invalid email format";
    }

    // Length validation
    if (!err && minLength && val.length < minLength) {
      err = `${label} must be at least ${minLength} ${type === "number" ? "digits" : "characters"}`;
    }
    if (!err && maxLength && val.length > maxLength) {
      alert(`${label} cannot exceed ${maxLength} digits`);
      return;
    }

    setInternalError(err);
    return !err;
  };

  const handleChange = (e) => {
    let val = e.target.value;

    if (type === "pancard" || type === "gst") {
      val = val.toUpperCase();
    }

    if (maxLength && val.length > maxLength) {
      setInternalError(`${label} cannot exceed ${maxLength} digits`);
      return;
    }

    if (validate(val)) {
      onChange(val);
    } else {
      onChange(val);
    }
  };

  // Dropdown
  if (type === "dropdown") {
    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        <div className="relative">
          <select
            value={value || ""}
            onChange={handleChange}
            disabled={disabled}
            className={`
    w-full px-3 py-1 pr-9 h-[34px]
    border ${displayError ? "border-red-500" : "border-gray-300"}
    ${disabled ? "bg-slate-50" : "bg-white"}
    rounded-md
    text-sm leading-6
    focus:outline-none focus:ring-2 focus:ring-blue-500
    appearance-none bg-none
    [&::-ms-expand]:hidden
  `}
          >
            <option value="">Select</option>
            {options.map((opt, idx) => (
              <option key={idx} value={opt.value || opt}>
                {formatText(opt.label) || formatText(opt)}
              </option>
            ))}
          </select>

          {/* Dropdown Icon */}
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
            <ChevronDown size={16} />
          </span>
        </div>

        {displayError && (
          <span className="text-red-500 text-xs mt-1">{displayError}</span>
        )}
      </div>
    );
  }

  if (type === "description") {
    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <textarea
          value={value || ""}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={`w-full px-3 py-1 border ${
            displayError ? "border-red-500" : "border-gray-300"
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
        />
        {displayError && (
          <span className="text-red-500 text-xs mt-1">{displayError}</span>
        )}
      </div>
    );
  }

  // Date Picker
  if (type === "date") {
    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type="date"
          value={value || ""}
          onChange={handleChange}
          min={min}
          max={max}
          className={`w-full px-3 py-1 border ${
            displayError ? "border-red-500" : "border-gray-300"
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        />
        {displayError && (
          <span className="text-red-500 text-xs mt-1">{displayError}</span>
        )}
      </div>
    );
  }

  // Normal Input
  return (
    <div className="w-full flex flex-col">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={
          ["string", "alphanumeric", "email", "pancard", "gst"].includes(type)
            ? "text"
            : type
        }
        value={value || ""}
        onChange={handleChange}
        disabled={disabled}
        maxLength={maxLength}
        placeholder={placeholder}
        className={`w-full px-3 py-1 border ${
          displayError ? "border-red-500" : "border-gray-300"
        } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
      />
      {displayError && (
        <span className="text-red-500 text-xs mt-1">{displayError}</span>
      )}
    </div>
  );
};

export default FormField;
